import type { ReactNode } from 'react';

import { Badge, Button } from '@/components/atoms';
import type { FlowStage, RunStageOut, StageFicha } from '@/services/engineering';

import styles from './NodePanel.module.css';

export interface NodePanelProps {
  stage: FlowStage;
  /** texto corrente do Role (editado ou não) */
  value: string;
  onChange: (v: string) => void;
  onRevert: () => void;
  onSave: () => void;
  onRestore: () => void;
  salvando: boolean;
  restaurando: boolean;
  out?: RunStageOut;
  /** laudo persistido, quando o backend serve o endpoint de edições */
  laudo?: ReactNode;
}

const NAO_SERVIDO = 'não informado pelo backend';

function linhasDaFicha(stage: FlowStage): { rotulo: string; valor: string }[] {
  const f: StageFicha = stage.ficha ?? {};
  const mostra = (v: unknown, sufixo = ''): string =>
    v === undefined || v === null || v === '' ? NAO_SERVIDO : `${String(v)}${sufixo}`;
  return [
    { rotulo: 'Modelo', valor: mostra(f.model ?? stage.model) },
    {
      rotulo: 'Temperatura',
      // A família gpt-5 ignora temperature: o backend manda null de propósito e
      // o painel diz "não se aplica" — não é ausência de informação.
      valor:
        f.temperatura === null && f.model
          ? 'não se aplica a esta família de modelo'
          : mostra(f.temperatura),
    },
    { rotulo: 'Esforço de raciocínio', valor: mostra(f.reasoning_effort) },
    { rotulo: 'Teto de tokens', valor: mostra(f.max_tokens) },
    { rotulo: 'Timeout', valor: mostra(f.timeout, ' s') },
    { rotulo: 'Retry (rede/5xx)', valor: mostra(f.retries) },
    { rotulo: 'Saída forçada por schema', valor: mostra(f.schema) },
    { rotulo: 'Custo estimado por matéria', valor: mostra(f.custo_estimado_usd, ' USD') },
  ];
}

/**
 * Painel inferior do nó selecionado (plano §11.1): Role editável (só nós de
 * IA), User montado como referência, saída da última execução e a ficha
 * técnica da chamada.
 *
 * Campo que o backend não serve mostra "não informado pelo backend". Preencher
 * com o default do plano seria mentir sobre o que roda na produção.
 */
export function NodePanel({
  stage,
  value,
  onChange,
  onRevert,
  onSave,
  onRestore,
  salvando,
  restaurando,
  out,
  laudo,
}: NodePanelProps) {
  const ehCodigo = stage.kind === 'codigo';
  // Nó de código com parâmetros (o gate do telejornal): edita-se o JSON.
  const ehParametros = ehCodigo && Boolean(stage.parametros);
  const editavel = !ehCodigo || ehParametros;
  const sujo = editavel && value !== (stage.role_system ?? '');

  return (
    <div className={styles.root}>
      <header className={styles.head}>
        <span className={styles.nome}>{stage.nome}</span>
        <Badge tone={ehCodigo ? 'neutral' : 'info'} size="xs">
          {ehCodigo ? 'nó de código' : 'nó de IA'}
        </Badge>
        {editavel && stage.is_overridden && (
          <Badge tone="warn" size="xs">
            {ehParametros ? 'Parâmetros salvos para este cliente' : 'Role com override salvo'}
          </Badge>
        )}
        <span className={styles.spacer} />
        <span className={styles.saida}>{stage.saida}</span>
      </header>

      <div className={styles.cols}>
        {/* ── coluna do prompt ── */}
        <section className={styles.col}>
          {!editavel ? (
            <>
              <span className={styles.label}>Nó determinístico</span>
              <p className={styles.explica}>
                Este estágio é código: não tem Role nem chamada de modelo. Ele aparece na
                linha para que o fluxo inteiro fique visível — o que ele faz está descrito
                em &ldquo;saída&rdquo;, no topo, e o resultado real da última execução está
                ao lado.
              </p>
            </>
          ) : (
            <>
              <div className={styles.labelRow}>
                <span className={styles.label}>
                  {ehParametros ? 'Parâmetros (JSON) — editável' : 'Role (system) — editável'}
                </span>
                <span className={styles.spacer} />
                {sujo && (
                  <button type="button" className={styles.linkBtn} onClick={onRevert}>
                    desfazer edição
                  </button>
                )}
              </div>
              <textarea
                className={styles.prompt}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                spellCheck={false}
                aria-label={`Role do estágio ${stage.nome}`}
              />
              <div className={styles.acoes}>
                <Button variant="primary" onClick={onSave} disabled={!sujo || salvando}>
                  {salvando ? 'A gravar…' : ehParametros ? 'Salvar parâmetros' : 'Salvar prompt'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Restaurar o prompt padrão de fábrica deste estágio? Remove a sua versão salva e a produção volta ao prompt original.',
                      )
                    )
                      onRestore();
                  }}
                  disabled={
                    restaurando ||
                    (!stage.is_overridden && value === (stage.default_system ?? ''))
                  }
                  title="Volta ao prompt de fábrica e remove o override salvo (backend + produção)"
                >
                  {restaurando ? 'A restaurar…' : 'Restaurar padrão'}
                </Button>
              </div>
            </>
          )}

          <div className={styles.userBloco}>
            <span className={styles.label}>User montado em runtime (referência)</span>
            <pre className={styles.userTpl}>
              {stage.user_template || 'O backend não serve o template deste estágio.'}
            </pre>
            {stage.nota && <p className={styles.nota}>{stage.nota}</p>}
          </div>
        </section>

        {/* ── coluna da saída + ficha ── */}
        <section className={styles.colSaida}>
          <span className={styles.labelSaida}>Saída da última execução</span>
          {!out && (
            <p className={styles.vazio}>
              Sem execução nesta sessão. Use &ldquo;Rodar teste&rdquo; no topo — sempre em
              simulação, sem publicar.
            </p>
          )}
          {out?.ok && <pre className={styles.out}>{out.output}</pre>}
          {out && !out.ok && <pre className={styles.outErr}>{out.error}</pre>}

          {laudo}

          {!ehCodigo && (
            <>
              <span className={styles.labelSaida}>Ficha técnica da chamada</span>
              <dl className={styles.ficha}>
                {linhasDaFicha(stage).map((l) => (
                  <div key={l.rotulo} className={styles.fichaLinha}>
                    <dt className={styles.fichaRot}>{l.rotulo}</dt>
                    <dd
                      className={`${styles.fichaVal} ${l.valor === NAO_SERVIDO ? styles.fichaAusente : ''}`}
                    >
                      {l.valor}
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
